import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useMediaQuery } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { change, Field, FieldArray, reduxForm } from 'redux-form';

import { Typography } from '@material-ui/core';

import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';

import {
  ACTIVITIES_AVAILABLE_PARTICIPANTS,
  ACTIVITY
} from 'graphql/queries/activities';
import {
  CREATE_ACTIVITY_BOOKING,
  UPDATE_ACTIVITY_BOOKING
} from 'graphql/mutations/activities-bookings';

import {
  AppHeader,
  CircularLoader,
  SingleDatePicker,
  DropDown,
  TextIconButton,
  CheckboxButtonsGroup,
  FormActionButtons,
  BookAccommodationDialog,
  BookInfoList,
  MultiDatePickerCalendar
} from 'components';

import ConfirmContext from 'contexts/ConfirmContext';

import StartTimeAndDurationRadioButtonsGroup from './components/start-time-and-duration-radio-buttons-group/StartTimeAndDurationRadioButtonsGroup';
import NumberOfParticipants from './components/number-of-participants/NumberOfParticipants';
import AddOnsList from './components/add-ons-list/AddOnsList';
import GuestInformation from './components/guest-information/GuestInformation';
import BookingSummary from './components/booking-summary/BookingSummary';
import BookRelatedField from './components/book-related-field/BookRelatedField';

import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';
import {
  getReceivedActivityBookingsDates,
  getReceivedActivityData
} from 'utils/accommodations-and-activities/prepareReceivedActivityData';
import { getParticipants } from 'utils/book-activity/prepareParticipants';
import { getPreparedBookActivityData } from 'utils/book-activity/prepareBookActivityData';
import {
  getAvailableRangeDates,
  getDefaultStartTime,
  getDisabledDates,
  getListOfStartTimeAndDuration,
  getListOfStartTimeAndDurationByCutoffPeriod
} from 'utils/book-activity/availableDates';
import {
  getAvailableParticipantsForStartTime,
  getMaxParticipants,
  getMaxParticipantsActivitySeveralDays
} from 'utils/book-activity/maxParticipants';

import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import validate from './validate';

import useStyles from './styles';
import { getRange } from '../../utils/book-accommodation/availableDates';

const initialValues = {
  maxParticipants: 0,
  currency: 'NOK',
  participants: [],
  addOns: [],
  mandatoryFees: [],
  accommodations: [],
  pay: {
    'Pay on arrival': true
  },
  scheduleType: null
};

const SCHEDULE_DATE_NO_TIME_NAME = 'scheduleDateForm';
const SCHEDULE_DATE_AND_TIME_NAME = 'scheduleDateTimeForm';
const SCHEDULE_PASS_NAME = 'schedulePassForm';
const FUTURE_PRICES = 'activityFuturePricesForm';

const BookActivity = ({
  bookActivityForm,
  changeFormFieldValue,
  handleSubmit,
  bookedData,
  isUpdate,
  businessOwner
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const selectedLanguage = i18n.language;
  const ownerId =
    businessOwner.selectedBusinessOwner.ownerId ||
    businessOwner.selectedBusinessOwner.id;

  const activityId = bookActivityForm.values.activityId;

  const initialStartingDate =
    bookedData && bookedData.startingDate
      ? bookedData.startingDate
      : bookedData && bookedData.multiDates
      ? bookedData.multiDates.startDate
      : moment();

  const [
    deletedRelatedAccommodations,
    setDeletedRelatedAccommodations
  ] = React.useState([]);
  const [relatedItem, setRelatedItem] = React.useState(null);
  const [isOpenBookDialog, setOpenBookDialog] = React.useState(false);
  const [isLoading, setLoading] = React.useState(null);
  const [activity, setActivity] = React.useState(null);
  const [isUpdatedParticipants, setIsUpdatedParticipants] = React.useState(
    false
  );
  const [
    listStartTimeAndDuration,
    setListStartTimeAndDuration
  ] = React.useState([]);
  const [listOfCurrencies, setListOfCurrencies] = React.useState([]);
  const [
    availableParticipantsData,
    setAvailableParticipantsData
  ] = React.useState(null);

  const [createActivityBooking] = useMutation(CREATE_ACTIVITY_BOOKING);
  const [updateActivityBooking] = useMutation(UPDATE_ACTIVITY_BOOKING);

  const { loading, data } = useQuery(ACTIVITY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityId: activityId || parseInt(id),
      businessOwnerId: ownerId
    }
  });

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useLayoutEffect(() => {
    const updateSize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const {
    loading: isAvailableParticipantsLoading,
    data: receivedAvailableParticipantsData,
    refetch: refetchParticipantsData
  } = useQuery(ACTIVITIES_AVAILABLE_PARTICIPANTS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityId: activityId || parseInt(id),
      businessOwnerId: ownerId,
      startPeriod: moment(initialStartingDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(initialStartingDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    if (!receivedAvailableParticipantsData) return;

    const availableParticipantsData = getReceivedActivityBookingsDates(
      receivedAvailableParticipantsData
    );

    setAvailableParticipantsData(availableParticipantsData);
  }, [receivedAvailableParticipantsData]);

  React.useEffect(() => {
    if (data && data.activities && data.activities.length) {
      const receivedActivity = data.activities[0];

      const activityData = getReceivedActivityData(receivedActivity);

      if (receivedActivity?.futurePrices?.length > 0) {
        const hasValidFuturePrices = receivedActivity.futurePrices.some(
          fp => fp.ratePlanPrices && fp.ratePlanPrices.length > 0
        );

        if (hasValidFuturePrices) {
          activityData['futurePriceForm'] = receivedActivity['futurePriceForm'];

          activityData['pricesType'] = receivedActivity['pricesType'];
        }
      } else {
        activityData['pricesType'] = receivedActivity['pricesType'];
      }

      activityData['pricesType'] = receivedActivity['pricesType'];
      activityData['scheduleType'] = receivedActivity['scheduleType'];

      changeFormFieldValue(`scheduleType`, activityData['scheduleType']);

      if (bookedData) {
        handleListStartTimeAndDurationField(activityData);
        setParticipants(activityData);
      }

      setListOfAvailableCurrencies(receivedActivity);
      setMaxParticipants(activityData['generalForm'].maxParticipants);

      setActivity(activityData);
      setAddOns(activityData);

      if (activityData.scheduleType === SCHEDULE_PASS_NAME) {
        setParticipants(activityData);
      }
    }
  }, [data]);

  React.useEffect(() => {
    const participants = bookActivityForm.values.participants;
    if (participants && participants.length) {
      setParticipants(activity);
    }
  }, [bookActivityForm.values.currency]);

  React.useEffect(() => {
    const multiDates = bookActivityForm.values.multiDates;
    if (multiDates) {
      handleMultiDateAvailableSeats(activity);
      setParticipants(activity);
    }
  }, [activity, availableParticipantsData, bookActivityForm.values.multiDates]);

  React.useEffect(() => {
    const startingDate = bookActivityForm.values.startingDate;

    if (startingDate) {
      handleListStartTimeAndDurationField(activity);
      setParticipants(activity);
    }
  }, [bookActivityForm.values.startingDate]);

  React.useEffect(() => {
    const startTime = bookActivityForm.values.startTime;

    if (startTime) {
      changeFormFieldValue('maxParticipants', startTime.seatsLeft);
    }
  }, [bookActivityForm.values.startTime]);

  const setListOfAvailableCurrencies = receivedActivity => {
    const listOfAvailableCurrencies = getListOfAvailableCurrencies(
      receivedActivity.prices
    );
    const selectedCurrency = bookActivityForm.values.currency;

    setListOfCurrencies(listOfAvailableCurrencies);

    if (!listOfAvailableCurrencies.includes(selectedCurrency)) {
      changeFormFieldValue('currency', listOfAvailableCurrencies[0]);
    }
  };

  const handleMultiDateAvailableSeats = activity => {
    if (!activity || !bookActivityForm.values.multiDates) return;

    const startDate = bookActivityForm.values.multiDates.startDate;
    const endDate = bookActivityForm.values.multiDates.endDate;

    if (!startDate || !endDate) return;

    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];

    const availableParticipants = getMaxParticipantsActivitySeveralDays(
      availableParticipantsPerDate,
      startDate,
      endDate,
      bookedData
    );

    if (availableParticipants !== null) {
      setMaxParticipants(availableParticipants);
    } else {
      setMaxParticipants(activity.generalForm.maxParticipants);
    }
  };

  const handleListStartTimeAndDurationField = activity => {
    if (!activity) return;

    const startingDate = bookActivityForm.values.startingDate;
    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];
    let listOfStartTimeAndDuration = getListOfStartTimeAndDuration(
      availableParticipantsPerDate,
      activity,
      bookActivityForm
    );
    listOfStartTimeAndDuration = getListOfStartTimeAndDurationByCutoffPeriod(
      activity,
      listOfStartTimeAndDuration,
      startingDate
    );
    let selectedStartTime = getDefaultStartTime(
      listOfStartTimeAndDuration,
      bookActivityForm
    );

    if (!selectedStartTime) {
      setMaxParticipants(activity.generalForm.maxParticipants);
      availableParticipantsPerDate.map(item => {
        const formattedStartDate = startingDate
          ? moment(startingDate).format('YYYY-MM-DD')
          : '';
        if (!item.startTime && item.date === formattedStartDate) {
          setMaxParticipants(item.availableParticipants);
        }
      });
    } else {
      setMaxParticipants(selectedStartTime.seatsLeft);

      listOfStartTimeAndDuration = getAvailableParticipantsForStartTime(
        listOfStartTimeAndDuration,
        bookedData,
        bookActivityForm,
        availableParticipantsData
      );
    }

    selectedStartTime = getDefaultStartTime(
      listOfStartTimeAndDuration,
      bookActivityForm
    );

    changeFormFieldValue('startTime', selectedStartTime);
    setListStartTimeAndDuration(listOfStartTimeAndDuration);
  };

  const setParticipants = activity => {
    const previousParticipants = bookActivityForm.values.participants || [];
    const participants = getParticipants(
      activity,
      bookActivityForm,
      setIsUpdatedParticipants,
      isUpdatedParticipants,
      bookActivityForm.values.currency,
      previousParticipants
    );

    changeFormFieldValue('participants', participants);
  };

  const setAddOns = activity => {
    const previousAddOns = bookActivityForm.values.addOns || [];

    const addOnsList = activity.addOnsForm.addOns.map(item => {
      const theSameAddOn = previousAddOns.find(addOn => addOn.id === item.id);
      return {
        ...item,
        value: theSameAddOn ? theSameAddOn.value : 0,
        originalId: theSameAddOn ? theSameAddOn.originalId : null
      };
    });

    changeFormFieldValue('addOns', addOnsList);
  };

  const setMaxParticipants = seatsLeft => {
    const maxParticipants = getMaxParticipants(
      seatsLeft,
      bookedData,
      bookActivityForm,
      availableParticipantsData
    );

    changeFormFieldValue('maxParticipants', maxParticipants);
  };

  const changeMonthHandler = date => {
    refetchParticipantsData({
      activityId: activityId || parseInt(id),
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const submit = () => {
    setLoading(true);

    const data = getPreparedBookActivityData(
      activity,
      bookActivityForm,
      deletedRelatedAccommodations
    );

    if (isUpdate) {
      updateActivityBooking({ variables: data }).then(
        () => {
          enqueueSnackbar(t('Activity booking has been successfully updated'), {
            variant: 'success'
          });
          history.push({
            pathname: '/bookings-list'
          });
        },
        () => {
          setLoading(false);
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    } else {
      createActivityBooking({ variables: data }).then(
        () => {

          enqueueSnackbar(t('Activity has been successfully booked'), {
            variant: 'success'
          });
          history.push({
            pathname: '/activities'
          });
        },
        () => {
          setLoading(false);
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    }
  };

  const getBookRelatedField = () => {
    const accommodation = bookActivityForm.values.accommodations[0];
    return (
      <BookRelatedField
        title={
          getFormByCurrentLanguage(
            selectedLanguage,
            accommodation,
            accommodation['Norwegian']
          ).title
        }
        addOns={accommodation.addOns}
        mandatoryFees={accommodation.mandatoryFees}
        deleteCallBack={() => deleteRelatedAccommodation(accommodation)}
        editCallBack={() => {
          setRelatedItem(accommodation);
          setOpenBookDialog(true);
        }}
      />
    );
  };

  const addRelatedAccommodation = accommodation => {
    setOpenBookDialog(false);
    changeFormFieldValue('accommodations', [accommodation]);
  };

  const deleteRelatedAccommodation = deleteItem => {
    confirm({
      title: 'Are you sure you want to delete it?',
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        changeFormFieldValue('accommodations', []);

        if (deleteItem.id) {
          setDeletedRelatedAccommodations([
            ...deletedRelatedAccommodations,
            deleteItem.id
          ]);
        }
      })
      .catch(() => {});
  };

  const cancelRelatedAccommodation = () => {
    confirm({
      title: 'You have unsaved changes',
      description: t('Are you sure you want to cancel?'),
      confirmationText: 'Cancel',
      cancellationText: 'Back'
    })
      .then(() => setOpenBookDialog(false))
      .catch(() => {});
  };

  const handlerSubmitWrapper = props => {
    if (bookActivityForm.syncErrors) {
      const syncErrors = bookActivityForm.syncErrors;
      const errorsLength = Object.keys(syncErrors).length;

      const availableParticipantsPerDate = availableParticipantsData
        ? availableParticipantsData.activities[0].availableParticipantsPerDate.filter(
            item => (item.startDate = bookActivityForm.startDate)
          )
        : [];

      if (syncErrors.isMaxParticipantsErrors && errorsLength === 2) {
        const error = syncErrors.participants['_error'];
        const errorSplit = error.split(' ');
        const errorLength = errorSplit.length;
        enqueueSnackbar(
          t('The number of Participants can’t exceed', {
            number: errorSplit[errorLength - 1]
          }),
          {
            variant: 'warning'
          }
        );
      } else {
        enqueueSnackbar(t('Please fill in all the required fields'), {
          variant: 'warning'
        });
      }
    }

    handleSubmit(submit)(props);
  };

  const updateRelatedAccommodation = accommodation => {
    setOpenBookDialog(false);
    changeFormFieldValue('accommodations', [
      ...bookActivityForm.values.accommodations.map(item => {
        if (item.accommodation === accommodation.accommodation) {
          return accommodation;
        }
        return item;
      })
    ]);
  };

  const bookedDates = availableParticipantsData => {
    data?.activities?.[0]?.activitybookingSet?.reduce(
      (acc, { endingDate, startingDate, isCanceled }) => {
        if (!isCanceled) {
          const effectiveEndingDate = endingDate || startingDate;
          return [
            ...acc,
            ...getRange(startingDate, effectiveEndingDate).map(v =>
              v.format('YYYY-MM-DD')
            )
          ];
        }
        const availableParticipantsPerDate = availableParticipantsData
          ? availableParticipantsData.activities[0].availableParticipantsPerDate
          : [];
        return acc.filter(
          bookedDate => !availableParticipantsPerDate.includes(bookedDate)
        );
      },
      []
    );
  };

  return (
    <>
      <AppHeader />

      {activity && isOpenBookDialog && (
        <BookAccommodationDialog
          open={isOpenBookDialog}
          ownerId={ownerId}
          onCancel={cancelRelatedAccommodation}
          onConfirm={addRelatedAccommodation}
          onUpdate={updateRelatedAccommodation}
          relatedAccommodationsForm={activity.relatedAccommodationsForm}
          currency={bookActivityForm.values.currency}
          initialValues={
            relatedItem || {
              participants: [],
              addOns: [],
              mandatoryFees: []
            }
          }
        />
      )}

      <CircularLoader
        isLoading={loading || isAvailableParticipantsLoading || isLoading}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />

      {activity && (
        <div className={classes.root}>
          <div className={classes.leftSide}>
            <Typography variant="h3" classes={{ root: classes.productCode }}>
              {t('Product code')}: {activity.generalForm.productCode}
            </Typography>

            <div
              className={classes.photo}
              style={{
                backgroundImage: `url(${activity.generalForm.coverPhoto.image})`
              }}
            >
              {''}
            </div>

            <Typography variant="h2" className={classes.fontWeight}>
              {
                getFormByCurrentLanguage(
                  selectedLanguage,
                  activity.generalForm,
                  activity['generalForm-Norwegian']
                ).title
              }
            </Typography>

            <div className={classes.listInfo}>
              <div className={classes.infoItem}>
                <LocationOnOutlinedIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {activity.generalForm.startingPoint}
                </Typography>
              </div>
            </div>

            <Typography variant="body1" classes={{ root: classes.overview }}>
              {
                getFormByCurrentLanguage(
                  selectedLanguage,
                  activity.generalForm,
                  activity['descriptionForm-Norwegian']
                ).overview
              }
            </Typography>

            <BookInfoList
              activity={activity}
              currency={bookActivityForm.values.currency}
              defaultExpanded={true}
            />
          </div>

          <div className={classes.rightSide}>
            <form
              onSubmit={handlerSubmitWrapper}
              className={classes.bookWrapper}
            >
              <Typography
                variant="h1"
                className={classNames(classes.fontWeight, classes.inputIndent)}
              >
                {isUpdate ? t('Update Activity Booking') : t('Book Activity')}
              </Typography>

              <Field
                name="currency"
                className={classNames(
                  classes.currencyInput,
                  classes.sectionIndent
                )}
                list={listOfCurrencies}
                label="Select currency"
                component={DropDown}
              />

              {activity.scheduleType === SCHEDULE_DATE_NO_TIME_NAME && (
                <Field
                  name="multiDates"
                  label="Starting date *"
                  availableRange={getAvailableRangeDates(activity)}
                  disabledDates={getDisabledDates(
                    availableParticipantsData,
                    bookedData && bookedData.multiDates,
                    activity
                  ).concat(bookedDates)}
                  changeMonthsCallback={changeMonthHandler}
                  numberOfMonths={
                    windowWidth > 1010 ? 2 : 1 || windowWidth <= 750 ? 1 : 2
                  }
                  daySize={windowWidth > 600 ? 38 : 41}
                  initialFocus="startDate"
                  dateRangePickerClass="dateRangeOpenedPicker"
                  inputClassName={classes.disableInputFocus}
                  component={MultiDatePickerCalendar}
                  cutoffData={activity[activity.scheduleType]}
                  isStartDateRenderMonth={
                    !!(bookedData && bookedData.multiDates)
                  }
                />
              )}

              {activity.scheduleType === SCHEDULE_DATE_AND_TIME_NAME && (
                <Field
                  name="startingDate"
                  label="Starting date *"
                  availableRange={getAvailableRangeDates(activity)}
                  disabledDates={getDisabledDates(
                    availableParticipantsData,
                    bookedData && bookedData.startingDate,
                    activity
                  ).concat(bookedDates)}
                  changeMonthsCallback={changeMonthHandler}
                  numberOfMonths={2}
                  daySize={39}
                  component={SingleDatePicker}
                  cutoffData={activity[activity.scheduleType]}
                  isStartDateRenderMonth={
                    !!(bookedData && bookedData.startingDate)
                  }
                />
              )}

              {activity.scheduleType === SCHEDULE_DATE_AND_TIME_NAME && (
                <Field
                  name="startTime"
                  label="Start time & Duration"
                  className={classes.sectionIndent}
                  list={listStartTimeAndDuration}
                  component={StartTimeAndDurationRadioButtonsGroup}
                />
              )}

              {!!bookActivityForm.values.participants.length && (
                <FieldArray
                  name="participants"
                  title="Number of Participants *"
                  isUpdatedParticipants={isUpdatedParticipants}
                  component={NumberOfParticipants}
                />
              )}

              {!!bookActivityForm.values.addOns.length && (
                <FieldArray
                  name="addOns"
                  title="Add-ons"
                  currency={bookActivityForm.values.currency}
                  component={AddOnsList}
                  addOnDescClass={classes.addOnDescClass}
                  addOnTitle={classes.addOnTitle}
                />
              )}

              <div className={classes.sectionIndent}>
                <Typography
                  variant="h2"
                  className={classes.accommodationsTitle}
                >
                  {t('Accommodation')}
                </Typography>
                {!bookActivityForm.values.accommodations.length && (
                  <TextIconButton
                    icon={<AddCircleOutlineIcon />}
                    text={t('Book')}
                    click={() => {
                      setRelatedItem(null);
                      setOpenBookDialog(true);
                    }}
                  />
                )}
                {!!bookActivityForm.values.accommodations.length &&
                  getBookRelatedField()}
              </div>

              <GuestInformation />

              <div className={classes.paymentWrapper}>
                <Typography variant="h2" className={classes.payment}>
                  {t('Payment')}
                </Typography>
                <Field
                  name="pay"
                  className={classes.paymentField}
                  disabledDays={['Pay on arrival']}
                  isDisabledChange={true}
                  component={CheckboxButtonsGroup}
                />
              </div>

              <BookingSummary
                relatedItems={bookActivityForm.values.accommodations}
                activity={activity}
                bookActivity={bookActivityForm.values}
                generalSectionTitle="Activity"
              />

              <FormActionButtons
                cancelPath={isUpdate ? '/bookings-list' : '/activities'}
                actionLabel={isUpdate ? 'Update' : 'Book'}
              />
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => {
  return {
    bookActivityForm: state.form.bookActivityForm,
    businessOwner: state.businessOwner
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('bookActivityForm', field, value));
  }
});

export default reduxForm({
  form: 'bookActivityForm',
  destroyOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(BookActivity));
